import { measureLatency } from '../gctl-measure';
import { AlbumFields, Album } from '../models/components/schemas/album/Album';
import {
  Select,
  NetworkError,
  deepCamelCase,
  deepSnakeCase,
  searchQueryString,
  intoSnakeCase,
} from '../fetchers/_helpers';

/**
 * This file was automatically generated by @gctl/openapi-fetchers
 * Do not modify this file, any changes will be destroyed when
 * the command is rerun.
 */

export type GetAlbumFields = readonly AlbumFields[];

/** Options for GET /albums/{album_id} */
export type GetAlbumOptions<Paths extends readonly string[]> = Omit<
  RequestInit,
  'method' | 'body'
> & {
  baseUrl?: string;

  /** Available fields on the response type, application/vnd.vimeo.album+json */
  select: Paths;
  where: {
    /**
     * The ID of the showcase.
     */
    albumId: number;
  };

  /** Possible query parameters for /albums/{album_id} */
  query?: {
    /**
     * The hashed or unhashed password of the showcase.
     */
    password?: string;

    /**
     * Whether to preview custom settings.
     */
    preview?: boolean;
  };
};

/** Result type for GET /albums/{album_id} */
export type GetAlbumResult<Paths extends readonly string[]> = Select<Album, Paths>;

/**
 * Get a specific showcase
 * This method returns the specified showcase. The authenticated user must be the owner of the showcase.
 */
export async function getAlbum<Paths extends GetAlbumFields>({
  baseUrl,
  select,
  where: { albumId },
  query,
  ...options
}: GetAlbumOptions<Paths>): Promise<GetAlbumResult<Paths>> {
  return measureLatency('getAlbum', 'GET', async () => {
    let res = await fetch(
      `${baseUrl}/albums/${albumId}?${searchQueryString(query)}&fields=${select
        .map(intoSnakeCase)
        .join(',')}`,
      {
        ...options,
        method: 'GET',
      },
    );

    if (!res.ok) {
      throw new NetworkError('A network error occurred', res.status, res);
    }

    if (res.status === 204) {
      return null as unknown as GetAlbumResult<Paths>;
    }

    if (!res.headers.get('content-type')?.match(/^application\/(.+)?json$/)) {
      throw new Error('Expected JSON response');
    }

    let json = await res.json();
    return deepCamelCase(json);
  });
}

export type DeleteAlbumFields = readonly string[];

/** Options for DELETE /albums/{album_id} */
export type DeleteAlbumOptions<Paths extends readonly string[]> = Omit<
  RequestInit,
  'method' | 'body'
> & {
  baseUrl?: string;

  where: {
    /**
     * The ID of the showcase.
     */
    albumId: number;
  };
};

/** Result type for DELETE /albums/{album_id} */
export type DeleteAlbumResult<Paths extends readonly string[]> = unknown;

/**
 * Delete a showcase
 * This method deletes the specified showcase. The authenticated user must be the owner of the showcase.
 */
export async function deleteAlbum<Paths extends DeleteAlbumFields>({
  baseUrl,
  where: { albumId },
  ...options
}: DeleteAlbumOptions<Paths>): Promise<DeleteAlbumResult<Paths>> {
  return measureLatency('deleteAlbum', 'DELETE', async () => {
    let res = await fetch(`${baseUrl}/albums/${albumId}`, {
      ...options,
      method: 'DELETE',
    });

    if (!res.ok) {
      throw new NetworkError('A network error occurred', res.status, res);
    }

    if (res.status === 204) {
      return null as unknown as DeleteAlbumResult<Paths>;
    }

    if (!res.headers.get('content-type')?.match(/^application\/(.+)?json$/)) {
      throw new Error('Expected JSON response');
    }

    let json = await res.json();
    return deepCamelCase(json);
  });
}

export type PatchAlbumFields = readonly AlbumFields[];

/** Options for PATCH /albums/{album_id} */
export type PatchAlbumOptions<Paths extends readonly string[]> = Omit<
  RequestInit,
  'method' | 'body'
> & {
  baseUrl?: string;

  /** Available fields on the response type, application/vnd.vimeo.album+json */
  select: Paths;
  where: {
    /**
     * The ID of the showcase.
     */
    albumId: number;
  };

  /** Data serialized to body for PATCH /albums/{album_id} */
  variables?: {
    /**
     * Whether the showcase permits continuous play.
     * @example true
     */

    allowContinuousPlay?: boolean;

    /**
     * Whether the showcase permits downloads.
     * @example true
     */

    allowDownloads?: boolean;

    /**
     * Whether the showcase permits sharing.
     * @example true
     */

    allowShare?: boolean;

    /**
     * Whether to start playback of the next video in the showcase's embedded playlist immediately after the previous video finishes.
     * @example true
     */

    autoplay?: boolean;

    /**
     * The hexadecimal color code for the color of the player buttons and showcase controls.
     * @example ff66ee
     */

    brandColor?: string;

    config?: {
      banner?: {
        /**
         * Whether to show the banner on the showcase page.
         * @example true
         */

        showBanner?: boolean;

        /**
         * Whether to show the banner description on the showcase page.
         * @example true
         */

        showBannerDescription?: boolean;

        /**
         * Whether to show the banner header on the showcase page.
         * @example true
         */

        showBannerHeader?: boolean;

        /**
         * Whether to show the banner title on the showcase page.
         * @example true
         */

        showBannerTitle?: boolean;

        /**
         * Whether to show the showcase owner's avatar on the showcase page.
         * @example true
         */

        showShowcaseOwnerAvatar?: boolean;
      };

      layout?: {
        /**
         * The grid size configuration.
         *
         * Option descriptions:
         *  * `l` - The grid size is large.
         *  * `m` - The grid size is medium.
         *  * `s` - The grid size is small.
         *  * `xl` - The grid size is extra large.
         *
         */

        gridSize?: `l` | `m` | `s` | `xl`;

        /**
         * Whether to show the profile image on each video.
         * @example true
         */

        showGridProfileImage?: boolean;

        /**
         * Whether to show the profile name on each video.
         * @example true
         */

        showGridProfileName?: boolean;

        /**
         * Whether to show the section names.
         * @example true
         */

        showGridSectionName?: boolean;

        /**
         * Whether to show the video count above the video list.
         * @example true
         */

        showGridVideoCount?: boolean;

        /**
         * Whether to show the video titles below thumbnails.
         * @example true
         */

        showGridVideoTitles?: boolean;
      };

      navigation?: {
        /**
         * Whether to show the search bar in the featured content layout.
         * @example true
         */

        showSearchBar?: boolean;
      };

      playback?: {
        /**
         * Whether to enable the comments feature in video playback.
         * @example true
         */

        enableComments?: boolean;

        /**
         * Whether to enable the download feature in video playback.
         * @example true
         */

        enableDownload?: boolean;

        /**
         * Whether to enable the like feature in video playback.
         * @example true
         */

        enableLike?: boolean;

        /**
         * Whether to enable the share feature in video playback.
         * @example true
         */

        enableShare?: boolean;

        /**
         * The grid size configuration for playback view.
         *
         * Option descriptions:
         *  * `l` - The grid size is large.
         *  * `m` - The grid size is medium.
         *  * `s` - The grid size is small.
         *  * `xl` - The grid size is extra large.
         *
         */

        playbackGridSize?: `l` | `m` | `s` | `xl`;

        /**
         * Information about showing the date when the video was added during playback.
         * @example true
         */

        showPlaybackAddedDate?: boolean;

        /**
         * Whether to show the profile image in grid view.
         * @example true
         */

        showPlaybackGridProfileImage?: boolean;

        /**
         * Whether to show the profile name in grid view.
         * @example true
         */

        showPlaybackGridProfileName?: boolean;

        /**
         * Whether to show the video card in grid view.
         * @example true
         */

        showPlaybackGridVideoCard?: boolean;

        /**
         * Whether to show the video title in grid view.
         * @example true
         */

        showPlaybackGridVideoTitle?: boolean;

        /**
         * Whether to show the profile image in video playback.
         * @example true
         */

        showPlaybackProfileImage?: boolean;

        /**
         * Whether to show the profile name in video playback.
         * @example true
         */

        showPlaybackProfileName?: boolean;

        /**
         * Whether to show the date when the video was uploaded in video playback.
         * @example true
         */

        showPlaybackUploadedDate?: boolean;

        /**
         * Whether to show the video count in video playback.
         * @example true
         */

        showPlaybackVideoCount?: boolean;

        /**
         * Whether to show the video description in video playback.
         * @example true
         */

        showPlaybackVideoDescription?: boolean;

        /**
         * Whether to show the video title in video playback.
         * @example true
         */

        showPlaybackVideoTitle?: boolean;

        /**
         * Whether to show the view count in video playback.
         * @example true
         */

        showPlaybackViews?: boolean;
      };

      /**
       * Whether to show the account name on the showcase page.
       * @example true
       */

      showAccountName?: boolean;

      /**
       * Whether to show the account avatar on the showcase page.
       * @example true
       */

      showAvatar?: boolean;

      /**
       * Whether to show the banner image on the showcase page.
       * @example true
       */

      showBanner?: boolean;

      /**
       * Whether to show the description on the showcase page.
       * @example true
       */

      showDescription?: boolean;

      /**
       * Whether to show the video details on the showcase page.
       * @example true
       */

      showVideoDetails?: boolean;

      /**
       * Whether to show the video player cards on the showcase page.
       * @example true
       */

      showVideoPlayerCards?: boolean;

      /**
       * The alignment of the call-to-action text on the showcase page.
       *
       * Option descriptions:
       *  * `center` - The text is centered.
       *  * `left` - The text is aligned to the left.
       *  * `right` - The text is aligned to the right.
       *
       */

      textCtaAlignment?: `center` | `left` | `right`;
    };

    /**
     * The description of the showcase.
     * @example Vimeo holiday videos!
     */

    description?: string;

    /**
     * The custom domain of the showcase.
     * @example mycustomdomain.com
     */

    domain?: null | string;

    /**
     * Whether to use the showcase's brand color in the player of the showcase's embedded playlist.
     * @example true
     */

    embedBrandColor?: boolean;

    /**
     * Whether to display the showcase's logo in the player of the showcase's embedded playlist.
     * @example true
     */

    embedCustomLogo?: boolean;

    /**
     * Whether to use the showcase's featured content in the web layout.
     * @example true
     */

    hasChosenThumbnail?: boolean;

    /**
     * Whether the showcase has featured content.
     */

    hasFeaturedContent?: boolean;

    /**
     * Whether the showcase should be hidden from Vimeo when unlisted.
     */

    hideFromVimeo?: boolean;

    /**
     * Whether to hide Vimeo navigation when displaying the showcase.
     * @example true
     */

    hideNav?: boolean;

    /**
     * Whether to include the upcoming event in the showcase.
     */

    hideUpcoming?: boolean;

    /**
     * Whether to hide the Vimeo logo in the player of the showcase's embedded playlist.
     * @example true
     */

    hideVimeoLogo?: boolean;

    /**
     * The type of layout for presenting the showcase.
     *
     * Option descriptions:
     *  * `grid` - The videos appear in a grid.
     *  * `player` - The videos appear in the player.
     *
     */

    layout?: `grid` | `player`;

    /**
     * Whether automatic playback restarts at the top of the showcase's embedded playlist after reaching the end of the last video in the playlist.
     * @example true
     */

    loop?: boolean;

    /**
     * The name of the showcase.
     * @example Vimeo Holiday Videos!
     */

    name?: string;

    /**
     * The showcase's password. This field is required only when **privacy** is `password`.
     * @example hunter1
     */

    password?: string;

    /**
     * The privacy level of the showcase.
     *
     * Option descriptions:
     *  * `anybody` - Anyone can access the showcase, either on Vimeo or through an embed.
     *  * `embed_only` - The showcase doesn't appear on Vimeo, but it can be embedded on other sites.
     *  * `nobody` - No one can access the showcase, including the authenticated user.
     *  * `password` - Only people with the password can access the showcase.
     *  * `team` - Only members of the authenticated user's team can access the showcase.
     *  * `unlisted` - The showcase can't be accessed if the URL omits its unlisted hash.
     *
     */

    privacy?: `anybody` | `embed_only` | `nobody` | `password` | `team` | `unlisted`;

    /**
     * Whether showcase videos use the review mode URL.
     * @example true
     */

    reviewMode?: boolean;

    /**
     * The genre or genres of the video in a Roku feed.
     */

    rokuGenres?: null | string[];

    /**
     * The ISO 639 Alpha-2 or Alpha-3 language code string that the Roku channel uses for all its information and descriptions.
     *
     * Option descriptions:
     *  * `de-DE` - The Roku langage is German.
     *  * `en` - The Roku language is English.
     *  * `es` - The Roku language is Spanish.
     *  * `fr-FR` - The Roku lanugage is French.
     *  * `ja-JP` - The Roku language is Japanese.
     *  * `ko-KR` - The Roku language is Korean.
     *  * `pt-BR` - The Roku language is Brazilian Portuguese.
     *
     */

    rokuLanguage?: null | `de-DE` | `en` | `es` | `fr-FR` | `ja-JP` | `ko-KR` | `pt-BR`;

    /**
     * The name of the Roku feed provider.
     * @example Roku Recommends
     */

    rokuProviderName?: null | string;

    /**
     * Whether search engines can index the showcase.
     */

    seoAllowIndexed?: boolean;

    /**
     * The SEO description of the showcase.
     */

    seoDescription?: null | string;

    /**
     * The SEO keywords of the showcase.
     */

    seoKeywords?: null | string[];

    /**
     * The SEO title of the showcase.
     */

    seoTitle?: null | string;

    /**
     * The default sort order of the videos as they appear in the showcase.
     *
     * Option descriptions:
     *  * `added_first` - The videos appear according to when they were added to the showcase, with the most recently added first.
     *  * `added_last` - The videos appear according to when they were added to the showcase, with the most recently added last.
     *  * `alphabetical` - The videos appear alphabetically by their title.
     *  * `alphabetical_desc` - The videos appear alphabetically by their title in reverse order.
     *  * `arranged` - The videos appear as arranged by the owner of the showcase.
     *  * `comments` - The videos appear according to their number of comments.
     *  * `likes` - The videos appear according to their number of likes.
     *  * `modified_time_asc` - The videos appear in ascending order based on their last modified time.
     *  * `modified_time_desc` - The videos appear in descending order based on their last modified time.
     *  * `newest` - The videos appear in chronological order with the newest first.
     *  * `oldest` - The videos appear in chronological order with the oldest first.
     *  * `plays` - The videos appear according to their number of plays.
     *
     */

    sort?:
      | `added_first`
      | `added_last`
      | `alphabetical`
      | `alphabetical_desc`
      | `arranged`
      | `comments`
      | `likes`
      | `modified_time_asc`
      | `modified_time_desc`
      | `newest`
      | `oldest`
      | `plays`;

    /**
     * The color theme of the showcase.
     *
     * Option descriptions:
     *  * `dark` - The showcase uses the dark theme.
     *  * `standard` - The showcase uses the standard theme.
     *
     */

    theme?: `dark` | `standard`;

    /**
     * The showcase's thumbnail type. The value of this field can be `0` when no thumbnail is chosen, `1` when a video or event's thumbnail is chosen, `2` when a specific frame from a video is chosen, and `3` when a custom image is chosen.
     * @example 1
     */

    thumbnailType?: number;

    /**
     * The custom Vimeo URL of the showcase.
     * @example my-custom-url
     */

    url?: null | string;

    /**
     * Whether the user has opted for a custom domain for their showcase.
     * @example true
     */

    useCustomDomain?: boolean;

    /**
     * Whether to use the showcase's brand color in the web layout.
     * @example true
     */

    webBrandColor?: boolean;

    /**
     * Whether to use the showcase's custom logo in the web layout.
     * @example true
     */

    webCustomLogo?: boolean;
  };
};

/** Result type for PATCH /albums/{album_id} */
export type PatchAlbumResult<Paths extends readonly string[]> = Select<Album, Paths>;

/**
 * Edit a showcase
 * This method edits the specified showcase. The authenticated user must be the owner of the showcase.
 */
export async function patchAlbum<Paths extends PatchAlbumFields>({
  baseUrl,
  select,
  variables,
  where: { albumId },
  ...options
}: PatchAlbumOptions<Paths>): Promise<PatchAlbumResult<Paths>> {
  return measureLatency('patchAlbum', 'PATCH', async () => {
    let res = await fetch(
      `${baseUrl}/albums/${albumId}?fields=${select.map(intoSnakeCase).join(',')}`,
      {
        ...options,
        method: 'PATCH',
        body: JSON.stringify(deepSnakeCase(variables)),
      },
    );

    if (!res.ok) {
      throw new NetworkError('A network error occurred', res.status, res);
    }

    if (res.status === 204) {
      return null as unknown as PatchAlbumResult<Paths>;
    }

    if (!res.headers.get('content-type')?.match(/^application\/(.+)?json$/)) {
      throw new Error('Expected JSON response');
    }

    let json = await res.json();
    return deepCamelCase(json);
  });
}
